import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, poundsFromPence } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useEvent } from "../lib/event";
import DashboardLayout from "../components/DashboardLayout";
import { ArrowUpRight, ArrowDownRight, Ticket, ShoppingBag, PoundSterling, Coins, Clock, CheckCircle2, Circle, X, Wand2, ChevronRight, Eye, CreditCard, Calendar, AlertTriangle } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

const CONV_DIST = (f) => [
  { label: "Paid", value: f.paid || 0 },
  { label: "Didn't book", value: Math.max(0, (f.visited || 0) - (f.paid || 0)) },
];

function ConversionCard({ eventId, initial }) {
  const RANGES = [["7", "7d"], ["30", "30d"], ["90", "90d"], ["all", "All"]];
  const [days, setDays] = useState("30");
  const [f, setF] = useState(initial || {});
  useEffect(() => {
    const qs = `days=${days}&attr=first${eventId ? `&event_id=${eventId}` : ""}`;
    api.get(`/dashboard/funnel?${qs}`).then((r) => setF(r.data)).catch(() => {});
  }, [days, eventId]);
  const steps = [
    { key: "visited", label: "Landed", icon: Eye },
    { key: "date", label: "Date picked", icon: Calendar },
    { key: "slot", label: "Time slot", icon: Clock, slotOnly: true },
    { key: "tickets", label: "Tickets added", icon: Ticket },
    { key: "checkout", label: "Reached checkout", icon: CreditCard },
    { key: "paid", label: "Paid", icon: CheckCircle2 },
  ].filter((s) => !s.slotOnly || f.slotted);
  const max = Math.max(1, f.visited || 0);
  return (
    <div className="st-card p-4" data-testid="conversion-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm uppercase tracking-[0.18em] text-[#8a8a8a]">Conversion Funnel</h3>
        <div className="flex gap-1 bg-[#0e0e0e] border border-[#1a1a1a] rounded-[5px] p-0.5" data-testid="funnel-range">
          {RANGES.map(([val, lbl]) => (
            <button key={val} onClick={() => setDays(val)} data-testid={`funnel-range-${val}`}
                    className={`px-2.5 py-1 rounded-[3px] text-xs transition-colors ${days === val ? "bg-[#dc2626] text-white" : "text-[#8a8a8a] hover:text-white"}`}>
              {lbl}
            </button>
          ))}
        </div>
      </div>
      <div className="flex flex-col lg:flex-row lg:items-center gap-4">
        {/* Left: conversion dial */}
        <div className="lg:w-40 shrink-0 lg:border-r lg:border-[#1a1a1a] lg:pr-4 flex flex-col justify-center">
          {f.visited ? (
            <div className="relative mx-auto" style={{ width: 118, height: 118 }} data-testid="conversion-dial">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={CONV_DIST(f)} dataKey="value" nameKey="label" cx="50%" cy="50%"
                       innerRadius={40} outerRadius={56} paddingAngle={2} startAngle={90} endAngle={-270} stroke="none">
                    {CONV_DIST(f).map((_, i) => <Cell key={i} fill={i === 0 ? "#dc2626" : "#242424"} />)}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-2xl font-bold text-[#dc2626] leading-none" data-testid="conversion-rate">{f.conversion_pct ?? 0}%</span>
                <span className="text-[9px] uppercase tracking-wider text-[#7a7a7a] mt-1">converted</span>
              </div>
            </div>
          ) : (
            <div className="text-center lg:text-left">
              <div className="text-4xl font-bold text-[#dc2626]" data-testid="conversion-rate">{f.conversion_pct ?? 0}%</div>
              <div className="text-[11px] text-[#6a6a6a] mt-2">Fills in as visitors browse your booking page.</div>
            </div>
          )}
        </div>

        {/* Middle: funnel step cards — one row on desktop, stacks on small screens */}
        <div className={`flex-1 grid grid-cols-2 sm:grid-cols-3 ${steps.length >= 6 ? "lg:grid-cols-6" : "lg:grid-cols-5"} gap-3`}>
          {steps.map((s, i) => {
            const val = f[s.key] || 0;
            const prev = i === 0 ? val : (f[steps[i - 1].key] || 0);
            const stepPct = i === 0 ? 100 : (prev ? Math.round((val / prev) * 100) : 0);
            const SIcon = s.icon;
            return (
              <div key={s.key} className="rounded-xl bg-[#0f0f0f] border border-[#1e1e1e] p-3" data-testid={`funnel-${s.key}`}>
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#7a7a7a]"><SIcon size={12} className="text-[#dc2626]" /> {s.label}</div>
                <div className="text-xl font-bold mt-1">{val}</div>
                <div className="mt-2 h-1 bg-[#161616] rounded-full overflow-hidden"><div className="h-full bg-[#dc2626]" style={{ width: `${Math.round((val / max) * 100)}%` }} /></div>
                <div className="text-[10px] text-[#7a7a7a] mt-1">{i === 0 ? "Entry point" : `${stepPct}% of previous`}</div>
              </div>
            );
          })}
        </div>
      </div>
      {(f.lost_tickets || 0) > 0 ? (
        <div className="mt-4 rounded-xl bg-[#1a0d0d] border border-[#3a1414] p-3 flex items-center gap-3" data-testid="funnel-at-risk">
          <div className="w-9 h-9 rounded-full bg-[#dc2626]/15 grid place-items-center shrink-0"><AlertTriangle size={16} className="text-[#dc2626]" /></div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-white">
              {f.lost_tickets} ticket{f.lost_tickets === 1 ? "" : "s"} at risk · {poundsFromPence(f.lost_revenue_pence || 0)} potential revenue
            </div>
            <div className="text-[11px] text-[#b98a8a]">Reached checkout but haven't paid yet — some may still return to book within the attribution window.</div>
          </div>
        </div>
      ) : null}
      {f.visited ? (
        <div className="mt-4 pt-3 border-t border-[#1a1a1a] flex items-center justify-between">
          <div className="text-[11px] text-[#7a7a7a]">Traffic sources, campaigns &amp; attribution</div>
          <Link to="/dashboard/reports" className="text-xs font-medium text-[#dc2626] hover:underline inline-flex items-center gap-1" data-testid="funnel-view-reports">
            View in Reports <ChevronRight size={13} />
          </Link>
        </div>
      ) : null}
    </div>
  );
}


function StatCard({ label, value, pct, icon: Icon, testid }) {
  const up = (pct ?? 0) >= 0;
  return (
    <div className="st-stat-card flex flex-col" data-testid={testid}>
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-full flex items-center justify-center shrink-0" style={{ background: "rgba(220,38,38,0.12)", border: "1px solid rgba(220,38,38,0.28)" }}>
          <Icon size={18} className="text-[#dc2626]" />
        </div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#8a8a8a]">{label}</div>
          <div className="text-2xl font-bold leading-tight mt-0.5 truncate">{value}</div>
        </div>
      </div>
      {pct != null && (
        <div className="mt-3 text-xs flex items-center gap-1">
          {up ? <ArrowUpRight size={13} className="text-[#f87171]" /> : <ArrowDownRight size={13} className="text-[#f87171]" />}
          <span className="text-[#f87171] font-semibold">{Math.abs(pct)}%</span>
          <span className="text-[#7a7a7a]">vs previous 30 days</span>
        </div>
      )}
    </div>
  );
}

export default function DashboardOverview() {
  const { user } = useAuth();
  const { currentEventId, currentEvent } = useEvent();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [attraction, setAttraction] = useState(null);
  const [chartMode, setChartMode] = useState(() => sessionStorage.getItem("st_chart_mode") || "day");
  const setMode = (m) => { setChartMode(m); sessionStorage.setItem("st_chart_mode", m); };
  const [swapOrders, setSwapOrders] = useState([]);
  const [checklist, setChecklist] = useState(null);
  const [hideChecklist, setHideChecklist] = useState(() => localStorage.getItem("st_hide_checklist") === "1");

  useEffect(() => {
    (async () => {
      const params = currentEventId ? `?event_id=${currentEventId}` : "";
      const [a, b, sp, cl] = await Promise.all([
        api.get(`/dashboard/overview${params}`),
        api.get("/attractions/me"),
        api.get("/orders?status=swap_pending&limit=10").catch(() => ({ data: { orders: [] } })),
        api.get("/attractions/me/checklist").catch(() => ({ data: null })),
      ]);
      setData(a.data); setAttraction(b.data); setSwapOrders(sp.data.orders || []); setChecklist(cl.data);
    })();
  }, [currentEventId]);

  const dismissChecklist = () => { localStorage.setItem("st_hide_checklist", "1"); setHideChecklist(true); };

  if (!data) return <DashboardLayout title="Loading…" />;

  // First-time experience (Change 3): show a welcoming hero instead of the checklist
  // until the wizard is completed OR at least one event exists.
  const showHero = !!checklist && !checklist.wizard_completed && (checklist.event_count || 0) === 0;

  return (
    <DashboardLayout
      title={<>Welcome back, <span className="text-[#dc2626]">{attraction?.name}</span></>}
      subtitle={currentEvent ? `Viewing: ${currentEvent.name}` : "Here's what's happening with your attraction."}
    >
      {showHero && (
        <div className="st-card p-10 mb-4 text-center st-fade-up" data-testid="first-time-hero">
          <div className="inline-flex w-14 h-14 rounded-full bg-[#160a0a] items-center justify-center text-[#dc2626] mb-4"><Wand2 size={26} /></div>
          <h2 className="text-2xl sm:text-3xl font-bold">Get your tickets ready to sell <span className="text-[#dc2626]">in under 2 minutes</span></h2>
          <p className="text-[#bbb] mt-3 max-w-xl mx-auto">We&apos;ll walk you through setting up your dates, times and ticket types — step by step.</p>
          <button onClick={() => navigate("/dashboard/events/new")} className="st-btn mt-6 inline-flex" data-testid="hero-start-wizard">Start Setup Wizard</button>
          <p className="text-xs text-[#7a7a7a] mt-3">or set up manually from the sidebar</p>
        </div>
      )}

      {!showHero && checklist && !(checklist.ready_to_sell && checklist.complete_count === checklist.total_count) && !hideChecklist && (
        <div className="st-card p-5 mb-4" style={{ borderLeft: "3px solid #dc2626" }} data-testid="onboarding-checklist">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h3 className="font-bold flex items-center gap-2">Get set up to sell {checklist.ready_to_sell && <span className="text-[10px] uppercase tracking-wider text-[#86efac] bg-[#0d2818] px-2 py-0.5 rounded">Ready to sell ✓</span>}</h3>
              <p className="text-xs text-[#8a8a8a] mt-0.5">{checklist.complete_count} of {checklist.total_count} complete · {checklist.required_done}/{checklist.required_total} required steps done</p>
            </div>
            <button onClick={dismissChecklist} className="text-[#7a7a7a] hover:text-white" data-testid="checklist-dismiss" title="Dismiss"><X size={16} /></button>
          </div>
          <div className="h-1.5 bg-[#1a1a1a] rounded-full mb-4 overflow-hidden">
            <div className="h-full bg-[#dc2626]" style={{ width: `${(checklist.complete_count / checklist.total_count) * 100}%` }} />
          </div>
          <div className="grid sm:grid-cols-2 gap-2">
            {checklist.items.map((it) => (
              <div key={it.id} className={`flex items-start gap-2.5 p-2.5 rounded-lg border ${it.complete ? "border-[#16331f] bg-[#0c1a10]" : "border-[#1a1a1a] bg-[#0e0e0e]"}`} data-testid={`checklist-item-${it.id}`}>
                {it.complete ? <CheckCircle2 size={16} className="text-[#86efac] mt-0.5 shrink-0" /> : <Circle size={16} className="text-[#5a5a5a] mt-0.5 shrink-0" />}
                <div className="min-w-0 flex-1">
                  <div className={`text-sm font-medium ${it.complete ? "text-[#8a8a8a] line-through" : ""}`}>{it.label}{it.required && !it.complete && <span className="text-[10px] text-[#dc2626] ml-1">required</span>}</div>
                  {!it.complete && <div className="text-xs text-[#7a7a7a]">{it.description}</div>}
                  {!it.complete && it.link && <Link to={it.link} className="st-link text-xs mt-1 inline-block" data-testid={`checklist-link-${it.id}`}>{it.link_label} →</Link>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {swapOrders.length > 0 && (
        <div className="st-card p-4 mb-4" style={{ borderLeft: "3px solid #facc15" }} data-testid="swap-pending-widget">
          <h3 className="text-sm uppercase tracking-[0.18em] text-[#facc15] flex items-center gap-2 mb-3">
            <Clock size={14} /> Awaiting swap payment ({swapOrders.length})
          </h3>
          <div className="space-y-2">
            {swapOrders.map((o) => (
              <Link key={o.id} to={`/dashboard/orders/${o.id}`}
                    className="flex items-center justify-between gap-3 p-2.5 rounded-lg bg-[#0e0e0e] border border-[#1a1a1a] hover:border-[#facc15] transition-colors"
                    data-testid={`swap-pending-${o.id}`}>
                <div className="truncate"><span className="text-[#dc2626] font-medium">#{o.order_number}</span><span className="text-[#dadada] ml-2">{o.customer_name}</span></div>
                <span className="text-xs text-[#8a8a8a] shrink-0">{poundsFromPence(o.gross_amount)} · chase before link expires</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="stats-grid">
        <StatCard testid="stat-total-sales" label="Total Sales" value={poundsFromPence(data.gross_pence)}
                  pct={data.trends?.gross_pct} icon={PoundSterling} />
        <StatCard testid="stat-tickets-sold" label="Tickets Sold" value={data.tickets_sold}
                  pct={data.trends?.tickets_pct} icon={Ticket} />
        <StatCard testid="stat-orders" label="Orders" value={data.orders_count}
                  pct={data.trends?.orders_pct} icon={ShoppingBag} />
        <StatCard testid="stat-net" label="Net Revenue" value={poundsFromPence(data.net_pence)}
                  pct={data.trends?.net_pct} icon={Coins} />
      </div>

      <div className="mt-4">
        <ConversionCard eventId={currentEventId} initial={data.funnel} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        <div className="lg:col-span-2 st-card p-4 flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm uppercase tracking-[0.18em] text-[#8a8a8a]">Sales over time</h3>
            <div className="flex gap-1 bg-[#0e0e0e] border border-[#1a1a1a] rounded-[5px] p-0.5" data-testid="chart-toggle">
              {["day", "week", "month"].map((m) => (
                <button key={m} onClick={() => setMode(m)} data-testid={`chart-${m}`}
                        className={`px-3 py-1 rounded-[3px] text-xs capitalize transition-colors ${chartMode === m ? "bg-[#dc2626] text-white" : "text-[#8a8a8a] hover:text-white"}`}>
                  {m}
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1 min-h-[200px]">
            <LineChart data={chartMode === "month" ? (data.sales_series_monthly || []) : chartMode === "week" ? (data.sales_series_weekly || []) : (data.sales_series || [])} mode={chartMode} />
          </div>
        </div>

        <div className="st-card p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm uppercase tracking-[0.18em] text-[#8a8a8a]">Upcoming Open Days</h3>
            <Link to="/dashboard/open-days" className="st-link text-xs" data-testid="view-all-open-days">View all</Link>
          </div>
          <div className="divide-y divide-[#1a1a1a] -mx-4" data-testid="upcoming-list">
            {data.upcoming.length === 0 && <p className="text-[#8a8a8a] text-sm px-4" data-testid="upcoming-empty">No upcoming open days. Add open days to get started.</p>}
            {data.upcoming.slice(0, 4).map((u) => {
              const cap = u.max_capacity || 0;
              const dt = new Date(u.date + "T12:00:00");
              const dayNum = dt.getDate();
              const wk = dt.toLocaleDateString("en-GB", { weekday: "short" }).toUpperCase();
              const mo = dt.toLocaleDateString("en-GB", { month: "short" }).toUpperCase();
              const longDate = dt.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
              return (
                <button key={u.id} onClick={() => navigate(`/dashboard/tickets?date=${u.id}`)} data-testid={`upcoming-${u.id}`}
                        className="w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-[#0e0e0e] transition-colors group">
                  {/* calendar box */}
                  <div className="w-12 py-2 rounded-lg bg-[#0f0f0f] border border-[#1e1e1e] flex flex-col items-center justify-center shrink-0">
                    <div className="text-[9px] font-bold text-[#dc2626] tracking-wider leading-none">{wk}</div>
                    <div className="text-lg font-bold leading-none my-1">{dayNum}</div>
                    <div className="text-[9px] font-bold text-[#dc2626] tracking-wider leading-none">{mo}</div>
                  </div>
                  {/* date + sold */}
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm truncate">{longDate}</div>
                    <div className="text-xs text-[#8a8a8a] mt-0.5">{u.tickets_sold} / {cap} tickets sold</div>
                    <div className="mt-1.5 h-[3px] w-full bg-[#1e1e1e] rounded-full overflow-hidden">
                      <div className="h-full bg-[#dc2626] rounded-full" style={{ width: `${u.fill_pct > 0 ? Math.max(6, u.fill_pct) : 0}%` }} />
                    </div>
                  </div>
                  {/* revenue + pct + chevron */}
                  <div className="text-right shrink-0">
                    <div className="text-[#dadada] font-semibold text-sm">{poundsFromPence(u.revenue_pence || 0)}</div>
                    <div className="text-xs text-[#7a7a7a] mt-0.5">{u.fill_pct}%</div>
                  </div>
                  <ChevronRight size={16} className="text-[#4a4a4a] group-hover:text-[#dc2626] transition-colors shrink-0" />
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <div className="st-card p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm uppercase tracking-[0.18em] text-[#8a8a8a]">Recent orders</h3>
            <Link to="/dashboard/orders" className="st-link text-xs" data-testid="view-all-orders">View all orders</Link>
          </div>
          <table className="w-full text-sm" data-testid="recent-orders-table">
            <thead className="text-left text-[10px] uppercase tracking-wider text-[#7a7a7a]">
              <tr><th className="py-2">Order</th><th>Customer</th><th>Amount</th><th>Status</th></tr>
            </thead>
            <tbody>
              {data.recent_orders.length === 0 && (
                <tr><td colSpan={4} className="text-[#8a8a8a] py-4 text-center">No orders yet.</td></tr>
              )}
              {data.recent_orders.map((o) => (
                <tr key={o.id} onClick={() => navigate(`/dashboard/orders/${o.id}`)}
                    className="border-t border-[#1a1a1a] cursor-pointer hover:bg-[#0e0e0e] transition-colors"
                    data-testid={`recent-order-${o.id}`}>
                  <td className="py-2.5 text-[#dc2626] font-medium">#{o.order_number}</td>
                  <td className="text-[#dadada]">{o.customer_name}</td>
                  <td className="font-semibold">{poundsFromPence(o.gross_amount)}</td>
                  <td><span className="st-chip st-chip-red">{o.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="st-card p-4">
          <h3 className="text-sm uppercase tracking-[0.18em] text-[#8a8a8a] mb-4">Your public booking page</h3>
          <div className="bg-[#0d0d0d] border border-[#1a1a1a] rounded-lg p-4 flex items-center justify-between">
            <code className="text-sm text-[#bbb]">scaretickets.co.uk/<span className="text-[#dc2626]">{attraction?.slug}</span></code>
            <a href={`/${attraction?.slug}`} target="_blank" rel="noreferrer" className="st-btn st-btn-ghost text-xs" data-testid="open-booking-page">Open</a>
          </div>
          <p className="text-xs text-[#8a8a8a] mt-3">Share this link to start selling. Embed code is in Settings → Booking Page.</p>
        </div>
      </div>
    </DashboardLayout>
  );
}

function ChartTooltip({ active, payload, mode }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  const periodLabel = mode === "month" ? "Month" : mode === "week" ? "Week of" : "Day";
  return (
    <div className="rounded-lg bg-[#0e0e0e] border border-[#1e1e1e] px-3 py-2 text-xs shadow-xl" data-testid="chart-tooltip">
      <div className="text-[10px] uppercase tracking-wider text-[#7a7a7a] mb-1">{periodLabel} · {p.label}</div>
      <div className="flex items-center justify-between gap-4"><span className="text-[#8a8a8a]">Sales</span><span className="font-semibold text-[#dc2626]">{poundsFromPence(p.total_pence)}</span></div>
      <div className="flex items-center justify-between gap-4"><span className="text-[#8a8a8a]">Tickets</span><span className="font-semibold">{p.tickets ?? 0}</span></div>
      <div className="flex items-center justify-between gap-4"><span className="text-[#8a8a8a]">Orders</span><span className="font-semibold">{p.orders ?? 0}</span></div>
    </div>
  );
}

function LineChart({ data, mode }) {
  if (!data?.length || data.every((d) => d.total_pence === 0)) {
    return <div className="h-full min-h-[200px] flex items-center justify-center text-[#7a7a7a] text-sm" data-testid="sales-chart">No sales data yet.</div>;
  }
  const gbp = (v) => `£${Math.round(v / 100).toLocaleString("en-GB")}`;
  return (
    <div className="h-full min-h-[200px]" data-testid="sales-chart">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="lcfill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#dc2626" stopOpacity="0.35" />
              <stop offset="1" stopColor="#dc2626" stopOpacity="0" />
            </linearGradient>
          </defs>
          <CartesianGrid stroke="#161616" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#6a6a6a" }} axisLine={false} tickLine={false} interval="preserveStartEnd" minTickGap={20} />
          <YAxis tickFormatter={gbp} tick={{ fontSize: 10, fill: "#6a6a6a" }} axisLine={false} tickLine={false} width={52}
                 label={{ value: "Order value", angle: -90, position: "insideLeft", style: { fill: "#6a6a6a", fontSize: 10, textAnchor: "middle" } }} />
          <RTooltip content={<ChartTooltip mode={mode} />} cursor={{ stroke: "#dc2626", strokeOpacity: 0.3 }} />
          <Area type="monotone" dataKey="total_pence" stroke="#dc2626" strokeWidth={2.5} fill="url(#lcfill)" dot={{ r: 2.5, fill: "#dc2626" }} activeDot={{ r: 4.5 }} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
